import { createTheme } from '@mui/material/styles';

// Enterprise design system for AQUATRON.
// One factory drives both light and dark so the two stay in lockstep.
// Palette: a restrained professional blue + cyan "aqua" accent on slate neutrals.
const FONT = `'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;
const MONO = `'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace`;

export function createAppTheme(mode = 'dark') {
  const dark = mode === 'dark';

  const brand = { main: dark ? '#3b82f6' : '#2563eb', light: '#60a5fa', dark: '#1d4ed8', contrastText: '#ffffff' };
  const accent = { main: dark ? '#22d3ee' : '#0891b2', light: '#67e8f9', dark: '#0e7490', contrastText: dark ? '#04222b' : '#ffffff' };

  const surface = dark
    ? { default: '#0b1220', paper: '#111a2e', elevated: '#16223c' }
    : { default: '#f6f8fb', paper: '#ffffff', elevated: '#ffffff' };

  const textColors = dark
    ? { primary: '#e6edf6', secondary: '#94a3b8', disabled: '#64748b' }
    : { primary: '#0f172a', secondary: '#5b6b7f', disabled: '#94a3b8' };

  const divider = dark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.10)';

  return createTheme({
    palette: {
      mode,
      primary: brand,
      secondary: accent,
      background: { default: surface.default, paper: surface.paper },
      text: textColors,
      divider,
      success: { main: dark ? '#34d399' : '#059669' },
      warning: { main: dark ? '#fbbf24' : '#d97706' },
      error: { main: dark ? '#f87171' : '#dc2626' },
      info: { main: dark ? '#38bdf8' : '#0284c7' },
      // custom tokens available via theme.palette.surface.*
      surface,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: FONT,
      fontSize: 14,
      h1: { fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.5rem' },
      h5: { fontWeight: 600, fontSize: '1.2rem' },
      h6: { fontWeight: 600, fontSize: '1.02rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, color: textColors.secondary },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.68rem' },
      fontFamilyMono: MONO,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: surface.default },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: dark ? 'rgba(148,163,184,0.25)' : 'rgba(15,23,42,0.18)',
            borderRadius: 8,
          },
          '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${divider}`,
            borderRadius: 12,
          },
          outlined: { border: `1px solid ${divider}` },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${divider}`,
            borderRadius: 12,
            boxShadow: dark ? '0 1px 2px rgba(0,0,0,0.4)' : '0 1px 2px rgba(15,23,42,0.05)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 16, minHeight: 38 },
          containedPrimary: {
            boxShadow: dark ? '0 1px 2px rgba(0,0,0,0.5)' : '0 1px 2px rgba(37,99,235,0.25)',
          },
          outlined: { borderColor: divider },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: dark ? 'rgba(17,26,46,0.85)' : 'rgba(255,255,255,0.9)',
            backdropFilter: 'saturate(180%) blur(8px)',
            color: textColors.primary,
            borderBottom: `1px solid ${divider}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: surface.paper,
            borderRight: `1px solid ${divider}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginInline: 8,
            marginBlock: 2,
            '&.Mui-selected': {
              backgroundColor: dark ? 'rgba(59,130,246,0.16)' : 'rgba(37,99,235,0.10)',
              color: brand.main,
              '& .MuiListItemIcon-root': { color: brand.main },
              '&:hover': { backgroundColor: dark ? 'rgba(59,130,246,0.22)' : 'rgba(37,99,235,0.16)' },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 38, color: textColors.secondary } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 7, fontWeight: 600 },
          sizeSmall: { height: 22 },
          outlined: { borderColor: divider },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              fontWeight: 700,
              color: textColors.secondary,
              backgroundColor: dark ? 'rgba(148,163,184,0.05)' : 'rgba(15,23,42,0.03)',
              borderBottom: `1px solid ${divider}`,
            },
          },
        },
      },
      MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${divider}`, paddingBlock: 10 } } },
      MuiTableContainer: { styleOverrides: { root: { border: `1px solid ${divider}`, borderRadius: 12 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8, '& fieldset': { borderColor: divider } },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: dark ? '#1e293b' : '#0f172a', fontSize: '0.75rem', borderRadius: 6 },
        },
      },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10, border: `1px solid ${divider}` } } },
      MuiDivider: { styleOverrides: { root: { borderColor: divider } } },
    },
  });
}

export default createAppTheme;
